import { useTranslation } from "react-i18next";
import { getUiLanguage, UI_LANGUAGES } from "../../i18n/languages";

const SEO_LANGUAGE_ROUTES = {
  "/": {
    fi: "/",
    en: "/en/",
  },
  "/en": {
    fi: "/",
    en: "/en/",
  },

  "/ominaisuudet": {
    fi: "/ominaisuudet",
    en: "/en/features",
  },
  "/en/features": {
    fi: "/ominaisuudet",
    en: "/en/features",
  },

  "/biisitoiveet-keikalle": {
    fi: "/biisitoiveet-keikalle",
    en: "/en/song-requests",
  },
  "/en/song-requests": {
    fi: "/biisitoiveet-keikalle",
    en: "/en/song-requests",
  },

  "/tietosuoja": {
    fi: "/tietosuoja",
    en: "/en/privacy",
  },
  "/en/privacy": {
    fi: "/tietosuoja",
    en: "/en/privacy",
  },

  "/kayttoehdot": {
    fi: "/kayttoehdot",
    en: "/en/terms",
  },
  "/en/terms": {
    fi: "/kayttoehdot",
    en: "/en/terms",
  },
};

function getCurrentPathname() {
  if (typeof window === "undefined") {
    return "/";
  }

  return window.location.pathname.replace(/\/+$/, "") || "/";
}

function getLocalizedSeoPath(languageCode) {
  const pathname = getCurrentPathname();
  const route = SEO_LANGUAGE_ROUTES[pathname];

  if (!route) {
    return null;
  }

  return route[languageCode] || null;
}

function FinnishFlag() {
  return (
    <span
      aria-hidden="true"
      className="relative block h-[12px] w-[18px] shrink-0 overflow-hidden rounded-[2px] bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.12)]"
    >
      <span className="absolute left-[5px] top-0 h-full w-[3px] bg-[#003580]" />
      <span className="absolute left-0 top-[4px] h-[3px] w-full bg-[#003580]" />
    </span>
  );
}

function BritishFlag() {
  return (
    <span
      aria-hidden="true"
      className="relative block h-[12px] w-[18px] shrink-0 overflow-hidden rounded-[2px] bg-[#012169] shadow-[0_0_0_1px_rgba(0,0,0,0.12)]"
    >
      <span className="absolute left-1/2 top-1/2 h-[2px] w-[24px] -translate-x-1/2 -translate-y-1/2 rotate-[34deg] bg-white" />
      <span className="absolute left-1/2 top-1/2 h-[2px] w-[24px] -translate-x-1/2 -translate-y-1/2 -rotate-[34deg] bg-white" />

      <span className="absolute left-1/2 top-1/2 h-[1px] w-[24px] -translate-x-1/2 -translate-y-1/2 rotate-[34deg] bg-[#C8102E]" />
      <span className="absolute left-1/2 top-1/2 h-[1px] w-[24px] -translate-x-1/2 -translate-y-1/2 -rotate-[34deg] bg-[#C8102E]" />

      <span className="absolute left-0 top-[4px] h-[4px] w-full bg-white" />
      <span className="absolute left-[7px] top-0 h-full w-[4px] bg-white" />

      <span className="absolute left-0 top-[5px] h-[2px] w-full bg-[#C8102E]" />
      <span className="absolute left-[8px] top-0 h-full w-[2px] bg-[#C8102E]" />
    </span>
  );
}

function LanguageFlag({ code }) {
  if (code === "fi") {
    return <FinnishFlag />;
  }

  if (code === "en") {
    return <BritishFlag />;
  }

  return null;
}

/**
 * Sama kielivalitsin toimii Hallinnassa ja julkisella puolella.
 *
 * SEO-sivuilla kielenvaihto vaihtaa myös kieliversion URL-osoitteen:
 * FI / EN.
 *
 * Muilla reiteillä, kuten /login, /admin ja /p/:slug,
 * vaihdetaan vain käyttöliittymän kieli kuten ennenkin.
 *
 * variant="public" näyttää kompaktin FI / EN -painikevalitsimen.
 *
 * showLabel={false} piilottaa Kieli / Language -tekstin silloin,
 * kun valitsin sijoitetaan esimerkiksi navigaation yhteyteen.
 *
 * Oletuksena Hallinnassa säilyy select-valitsin.
 */
export default function LanguageSwitcher({
  className = "",
  variant = "select",
  showLabel = true,
}) {
  const { t, i18n } = useTranslation("common");
  const language = getUiLanguage(i18n.resolvedLanguage || i18n.language);

  function changeLanguage(languageCode) {
    if (!languageCode || languageCode === language.code) {
      return;
    }

    const localizedSeoPath = getLocalizedSeoPath(languageCode);

    if (localizedSeoPath) {
      try {
        window.localStorage.setItem(
          "paranoidi_ui_language",
          languageCode
        );
      } catch {
        // Kielireitti toimii myös ilman localStoragea.
      }

      const targetUrl =
        localizedSeoPath +
        window.location.search +
        window.location.hash;

      window.location.assign(targetUrl);
      return;
    }

    void i18n.changeLanguage(languageCode);
  }

  if (variant === "public") {
    return (
      <div
        className={`inline-flex flex-wrap items-center justify-center gap-2.5 lg:justify-start ${className}`}
      >
        {showLabel && (
          <span className="text-[0.6rem] font-black uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-zinc-500">
            {t("language.label")}
          </span>
        )}

        <div className="inline-flex items-center rounded-full border border-[#5a3824]/15 bg-white/55 p-1 shadow-[0_6px_20px_rgba(58,43,24,0.08)] backdrop-blur-sm theme-dark:border-white/10 theme-dark:bg-white/[0.035] theme-dark:shadow-[0_6px_20px_rgba(0,0,0,0.28)]">
          {UI_LANGUAGES.map((item) => {
            const isActive = item.code === language.code;

            return (
              <button
                key={item.code}
                type="button"
                lang={item.code}
                aria-pressed={isActive}
                aria-label={`${t("language.label")}: ${item.nativeName}`}
                onClick={() => changeLanguage(item.code)}
                className={[
                  "group flex h-8 items-center gap-1.5 rounded-full px-2.5",
                  "text-[0.68rem] font-black uppercase tracking-[0.08em]",
                  "outline-none transition-all duration-300 ease-out",
                  "focus-visible:ring-2 focus-visible:ring-red-400",
                  isActive
                    ? "scale-[1.02] bg-[#541717] text-white shadow-[0_5px_16px_rgba(127,29,29,0.22)] theme-dark:bg-red-500/20 theme-dark:text-red-100 theme-dark:shadow-[0_0_18px_rgba(239,68,68,0.16)]"
                    : "text-[#8a5a35] hover:bg-[#5a3824]/5 hover:text-[#541717] theme-dark:text-zinc-500 theme-dark:hover:bg-white/[0.06] theme-dark:hover:text-white",
                ].join(" ")}
              >
                <span
                  className={[
                    "transition-transform duration-300",
                    isActive
                      ? "scale-105"
                      : "opacity-75 group-hover:scale-105 group-hover:opacity-100",
                  ].join(" ")}
                >
                  <LanguageFlag code={item.code} />
                </span>

                <span>{item.code.toUpperCase()}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <label
      className={`inline-flex max-w-full flex-wrap items-center gap-3 text-sm font-bold ${className}`}
    >
      {showLabel && <span>{t("language.label")}</span>}

      <select
        value={language.code}
        onChange={(event) => changeLanguage(event.target.value)}
        aria-label={!showLabel ? t("language.label") : undefined}
        className="min-w-0 rounded-xl border border-[#5a3824]/25 bg-white px-3 py-2 text-sm font-bold text-[#241a16] outline-none focus-visible:ring-2 focus-visible:ring-red-400 theme-dark:border-white/20 theme-dark:bg-zinc-900 theme-dark:text-white"
      >
        {UI_LANGUAGES.map((item) => (
          <option key={item.code} value={item.code} lang={item.code}>
            {item.nativeName}
          </option>
        ))}
      </select>
    </label>
  );
}